import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
  Clock,
  DollarSign,
  Hash,
  Cpu,
  FileText,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Calendar,
  Search,
  Download,
  Eye,
  EyeOff
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TestSession, TestCase, ModelResponse } from '@/types/ai-admin';

interface ResponseHistoryProps {
  sessions: TestSession[];
  testCases: TestCase[];
}

export default function ResponseHistory({ sessions, testCases }: ResponseHistoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [showDetails, setShowDetails] = useState(true);

  // Filter sessions
  const filteredSessions = sessions.filter(session => {
    const matchesSearch = searchQuery === '' ||
      session.responses.some(r =>
        r.query.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.response.toLowerCase().includes(searchQuery.toLowerCase())
      );

    const matchesDate = (() => {
      if (dateRange === 'all') {
return true;
}
      const sessionDate = new Date(session.timestamp);
      const now = new Date();
      const daysDiff = (now.getTime() - sessionDate.getTime()) / (1000 * 60 * 60 * 24);

      switch (dateRange) {
        case 'today': return daysDiff < 1;
        case '7days': return daysDiff < 7;
        case '30days': return daysDiff < 30;
        default: return true;
      }
    })();

    const matchesModel = modelFilter === 'all' ||
      session.responses.some(r => r.model.includes(modelFilter));

    return matchesSearch && matchesDate && matchesModel;
  }).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  // Toggle session expansion
  const toggleSession = (sessionId: string) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  // Export session data
  const exportSession = (session: TestSession) => {
    const exportData = {
      session,
      testCases: session.testCases.map(tcId =>
        testCases.find(tc => tc.id === tcId)
      ).filter(Boolean),
      exportDate: new Date().toISOString()
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${ encodeURIComponent(dataStr)}`;
    const exportFileDefaultName = `test_session_${session.id}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Get model badge color
  const getModelBadgeColor = (model: string) => {
    if (model.includes('haiku')) {
return 'bg-blue-100 text-blue-800';
}
    if (model.includes('sonnet')) {
return 'bg-purple-100 text-purple-800';
}
    if (model.includes('opus')) {
return 'bg-orange-100 text-orange-800';
}
    return 'bg-gray-100 text-gray-800';
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Test History</CardTitle>
              <CardDescription>
                Review past test sessions and analyze results
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? (
                <>
                  <EyeOff className="mr-2 h-4 w-4" />
                  Hide Details
                </>
              ) : (
                <>
                  <Eye className="mr-2 h-4 w-4" />
                  Show Details
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search queries or responses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Date Range */}
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>

            {/* Model Filter */}
            <Select value={modelFilter} onValueChange={setModelFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Models</SelectItem>
                <SelectItem value="haiku">Haiku</SelectItem>
                <SelectItem value="sonnet">Sonnet</SelectItem>
                <SelectItem value="opus">Opus</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Sessions List */}
      <div className="space-y-4">
        {filteredSessions.length > 0 ? (
          filteredSessions.map((session) => {
            const isExpanded = expandedSessions.has(session.id);
            const successCount = session.responses.filter(r => r.success).length;
            const successRate = (successCount / session.responses.length) * 100;

            return (
              <Card key={session.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => toggleSession(session.id)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                      <div>
                        <div className="flex items-center space-x-2">
                          <CardTitle className="text-base">
                            Test Session #{session.id.split('_').pop()?.slice(-6)}
                          </CardTitle>
                          <Badge variant="outline" className="text-xs">
                            {session.responses.length} queries
                          </Badge>
                          {successRate >= 90 ? (
                            <Badge className="bg-green-100 text-green-800 text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {successRate.toFixed(0)}% pass
                            </Badge>
                          ) : successRate >= 70 ? (
                            <Badge className="bg-orange-100 text-orange-800 text-xs">
                              {successRate.toFixed(0)}% pass
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 text-xs">
                              <XCircle className="h-3 w-3 mr-1" />
                              {successRate.toFixed(0)}% pass
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {formatTimestamp(session.timestamp)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 text-sm">
                      {/* Session Metrics */}
                      <div className="flex items-center space-x-1 text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{(session.totalDuration / 1000).toFixed(1)}s</span>
                      </div>
                      <div className="flex items-center space-x-1 text-muted-foreground">
                        <DollarSign className="h-3 w-3" />
                        <span>${session.totalCost.toFixed(4)}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSession(session)}
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent>
                    <Separator className="mb-4" />
                    <ScrollArea className={cn(
                      "w-full",
                      showDetails ? "h-[400px]" : "h-auto"
                    )}>
                      <div className="space-y-3">
                        {session.responses.map((response, idx) => {
                          const testCase = response.testCaseId
                            ? testCases.find(tc => tc.id === response.testCaseId)
                            : null;

                          return (
                            <div key={response.id} className="border rounded-lg p-3 space-y-2">
                              {/* Query Header */}
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-medium">Query {idx + 1}</span>
                                    {testCase && (
                                      <Badge variant="outline" className="text-xs">
                                        {testCase.name}
                                      </Badge>
                                    )}
                                    <Badge className={cn("text-xs", getModelBadgeColor(response.model))}>
                                      {response.model.split('-').pop()}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {response.query}
                                  </p>
                                </div>
                                {response.success ? (
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-600" />
                                )}
                              </div>

                              {showDetails && (
                                <>
                                  {/* Response Preview */}
                                  <div className="p-2 bg-muted rounded text-sm">
                                    <p className="line-clamp-3">{response.response}</p>
                                  </div>

                                  {/* Metrics */}
                                  <div className="flex flex-wrap gap-2 text-xs">
                                    <div className="flex items-center space-x-1">
                                      <Clock className="h-3 w-3 text-muted-foreground" />
                                      <span>{(response.responseTime / 1000).toFixed(2)}s</span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <DollarSign className="h-3 w-3 text-muted-foreground" />
                                      <span>${response.cost.toFixed(4)}</span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <Hash className="h-3 w-3 text-muted-foreground" />
                                      <span>{response.tokensInput}/{response.tokensOutput}</span>
                                    </div>
                                    {response.score && (
                                      <div className="flex items-center space-x-1">
                                        <TrendingUp className="h-3 w-3 text-muted-foreground" />
                                        <span>{(response.score * 100).toFixed(0)}%</span>
                                      </div>
                                    )}
                                    {response.documentsUsed && response.documentsUsed.length > 0 && (
                                      <div className="flex items-center space-x-1">
                                        <FileText className="h-3 w-3 text-muted-foreground" />
                                        <span>{response.documentsUsed.length} docs</span>
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>

                    {/* Session Summary */}
                    <Separator className="my-4" />
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Avg Response Time</p>
                        <p className="font-medium">
                          {(session.responses.reduce((sum, r) => sum + r.responseTime, 0) /
                            session.responses.length / 1000).toFixed(2)}s
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Avg Cost</p>
                        <p className="font-medium">
                          ${(session.totalCost / session.responses.length).toFixed(4)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total Tokens</p>
                        <p className="font-medium">
                          {session.responses.reduce((sum, r) =>
                            sum + (r.tokensInput || 0) + (r.tokensOutput || 0), 0
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Models Used</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Array.from(new Set(session.responses.map(r => r.model))).map(model => (
                            <Badge key={model} variant="outline" className="text-xs">
                              {model.split('-').pop()}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchQuery || dateRange !== 'all' || modelFilter !== 'all'
                  ? 'No test sessions found matching your filters'
                  : 'No test sessions recorded yet'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Run tests to see them appear here
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Summary Statistics */}
      {filteredSessions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Period Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Total Sessions</p>
                <p className="text-2xl font-bold">{filteredSessions.length}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Total Queries</p>
                <p className="text-2xl font-bold">
                  {filteredSessions.reduce((sum, s) => sum + s.responses.length, 0)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Total Cost</p>
                <p className="text-2xl font-bold">
                  ${filteredSessions.reduce((sum, s) => sum + s.totalCost, 0).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Avg Success Rate</p>
                <p className="text-2xl font-bold">
                  {(filteredSessions.reduce((sum, s) => sum + s.successRate, 0) /
                    filteredSessions.length).toFixed(1)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}